import { ArrowUpRight } from 'lucide-react'

import { cn } from '../../lib/utils'

export type CurrentIngestState = 'question' | 'failed' | 'running' | 'complete'

export interface CurrentIngestDescriptor {
  title: string
  state: CurrentIngestState
  onSelect: () => void
  /** Where the click goes. Defaults to leaving for the full session page. */
  hint?: string
}

/**
 * A run that is neither pending nor failed has finished: the branch adoption
 * outcome is only ever pending, success, or failed once a session exists, so
 * "not running, not broken" is success rather than an unknown resting state.
 */
export function currentIngestState({
  pendingQuestion,
  failed,
  active
}: {
  pendingQuestion: boolean
  failed: boolean
  active: boolean
}): CurrentIngestState {
  if (pendingQuestion) return 'question'
  if (failed) return 'failed'
  if (active) return 'running'
  return 'complete'
}

export function currentIngestTitle(
  runName: string | undefined,
  fileName?: string | undefined
): string {
  const named = runName?.trim()
  if (named) return named
  const fallback = fileName?.trim()
  return fallback || 'Ingest'
}

/**
 * The state lives in a badge rather than in the row's own colour. A tinted or
 * filled row competes with the grey fill that marks the open conversation, and
 * this run is never the open conversation — clicking it leaves the chat page.
 *
 * Hand-rolled rather than a StatusPill because a running job wears the session
 * hue, matching the rail tab for the same run, and StatusPill has no such tone.
 */
const BADGE: Record<CurrentIngestState, { label: string; className: string }> = {
  question: {
    label: 'Needs review',
    className: 'bg-st-warn-bg text-st-warn'
  },
  failed: { label: 'Failed', className: 'bg-st-down-bg text-st-down' },
  running: { label: 'Running', className: 'bg-sess-bg text-sess' },
  complete: { label: 'Complete', className: 'bg-st-run-bg text-st-run' }
}

/** Only unfinished work flashes — a settled run should stop asking for eyes. */
const DOT: Record<CurrentIngestState, string> = {
  question: 'animate-pulse bg-st-warn',
  failed: 'bg-st-down',
  running: 'animate-pulse bg-sess',
  complete: 'bg-st-run'
}

const DOT_LABEL: Record<CurrentIngestState, string> = {
  question: 'Pending question',
  failed: 'Ingest session failed',
  running: 'Ingest session running',
  complete: 'Ingest session complete'
}

/**
 * The active ingest run, pinned above the conversation groups. It is not a
 * conversation the chat page can open — selecting it leaves for the job page,
 * which is the session's only full-size home.
 */
export function CurrentIngestEntry({ title, state, onSelect, hint }: CurrentIngestDescriptor) {
  const badge = BADGE[state]

  return (
    <div>
      <div className="px-2 py-1 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
        Current Ingest
      </div>
      <div
        role="button"
        tabIndex={0}
        title={title}
        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-foreground/5"
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect()
          }
        }}
      >
        <span
          className={cn('size-1.5 shrink-0 rounded-full', DOT[state])}
          aria-label={DOT_LABEL[state]}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">{title}</span>
          {/* Says where the click goes, since every other row in this list stays put. */}
          <span className="block text-xs text-muted-foreground">{hint ?? 'Open full session'}</span>
        </span>
        <span
          className={cn(
            'shrink-0 rounded px-1.5 py-0.5 text-10 font-semibold tracking-[0.06em] uppercase',
            badge.className
          )}
        >
          {badge.label}
        </span>
        <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </div>
    </div>
  )
}
